import { themeAtom } from "../../../shared/atoms";
import {
  AdminCertificatesSettingsPayload,
  AdminCertificatesSettingsRow,
} from "../../models/admin-certificates-settings.models";
import {
  getAdminCertificatesSettings,
  getAdminCertificatesSettingsErrorMessage,
  updateAdminCertificatesSettings,
} from "../../services/admin-certificates-settings.service";
import { toAdminCertificatesSettingsGridRow } from "../../utils/admin-certificates-settings.utils";
import AdminCertificatesSettingsModal from "./AdminCertificatesSettingsModal";
import { LoadingState } from "./sub-components/helpers/admin-certificates-settings-panel.types";
import AdminCertificatesSettingsGrid from "./sub-components/panel/AdminCertificatesSettingsGrid";
import AdminCertificatesSettingsStatus from "./sub-components/panel/AdminCertificatesSettingsStatus";
import AdminCertificatesSettingsTopBar from "./sub-components/panel/AdminCertificatesSettingsTopBar";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

const AdminCertificatesSettingsPanel = () => {
  const theme = useAtomValue(themeAtom);
  const [rows, setRows] = useState<AdminCertificatesSettingsRow[]>([]);
  const [vesselTypeOptions, setVesselTypeOptions] = useState<string[]>([]);
  const [editingRow, setEditingRow] = useState<AdminCertificatesSettingsRow | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [modalErrorMessage, setModalErrorMessage] = useState<string | null>(null);
  const [isModalSaving, setIsModalSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoadingState("loading");
    setErrorMessage(null);

    try {
      const response = await getAdminCertificatesSettings();
      setRows(response.rows.map(toAdminCertificatesSettingsGridRow));
      setVesselTypeOptions(response.vesselTypeOptions ?? []);
      setLoadingState("ready");
    } catch (error) {
      setErrorMessage(getAdminCertificatesSettingsErrorMessage(error));
      setLoadingState("error");
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const handleEditRow = useCallback((row: AdminCertificatesSettingsRow) => {
    setEditingRow(row);
    setModalErrorMessage(null);
  }, []);

  const handleModalSave = useCallback(
    async (payload: AdminCertificatesSettingsPayload) => {
      if (!editingRow) {
        return;
      }

      setIsModalSaving(true);
      setModalErrorMessage(null);

      try {
        const response = await updateAdminCertificatesSettings(editingRow.ID, payload);
        const savedRow = toAdminCertificatesSettingsGridRow(response.row);
        setRows((current) => current.map((row) => (row.ID === savedRow.ID ? savedRow : row)));
        setEditingRow(null);
      } catch (error) {
        setModalErrorMessage(getAdminCertificatesSettingsErrorMessage(error));
      } finally {
        setIsModalSaving(false);
      }
    },
    [editingRow],
  );

  const rowCountLabel = useMemo(() => `${rows.length} row${rows.length === 1 ? "" : "s"}`, [rows.length]);

  return (
    <main
      data-test="admin-certificates-settings-page"
      className={twMerge("flex min-h-0 min-w-0 flex-1 overflow-hidden", theme.home.tailwind.textDefault)}
    >
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col" data-test="admin-certificates-settings-admin">
        <AdminCertificatesSettingsTopBar
          rowCountLabel={rowCountLabel}
          isRefreshing={loadingState === "loading"}
          onRefresh={loadSettings}
        />
        <AdminCertificatesSettingsStatus errorMessage={errorMessage} loadingState={loadingState} />
        <AdminCertificatesSettingsGrid rows={rows} loadingState={loadingState} onEditRow={handleEditRow} />
      </div>
      {editingRow && (
        <AdminCertificatesSettingsModal
          row={editingRow}
          vesselTypeOptions={vesselTypeOptions}
          saving={isModalSaving}
          errorMessage={modalErrorMessage}
          onCancel={() => {
            if (!isModalSaving) {
              setEditingRow(null);
              setModalErrorMessage(null);
            }
          }}
          onSave={handleModalSave}
        />
      )}
    </main>
  );
};

export default AdminCertificatesSettingsPanel;
